import React from 'react';
import { Box, Button, CircularProgress, IconButton, Stack, Tooltip } from '@mui/material';
import { theme } from '../../../../../../common/styles/theme';
import { Text } from '../../../../../../common/components/Text/Text';
import { Tabs } from '../../../../../../common/components/Tabs/Tabs';
import { Tab } from '../../../../../../common/components/Tab/Tab';
import { pillTabsSx } from '../../../../../../common/components/Tabs/pillTabsSx';
import { Paper } from '../../../../../../common/components/Paper/Paper';
import { SpeakerIcon } from '../../../../../../../icons/SpeakerIcon';
import { TranslationsIcon } from '../../../../../../../icons/TranslationsIcon';
import { textSpeaker } from '../../../../../effects/textSpeaker';
import { TimestampButton } from '../../TimestampButton/TimestampButton';
import { NativeTooltip } from '../../NativeTooltip/NativeTooltip';
import { TWordReason } from '../../../../../../common/rest/words/wordsRest';
import { collectWords, groupWordsByLemma, LANGUAGE_BUCKET_LABELS, TEXT_BUCKET_LABELS, TFrequencyTab, tokenizeCaptions } from '../../../../../../common/utils/wordFrequencyUtil';
import { IWordsTabProps } from './types';

const REASON_LABEL: Record<TWordReason, string> = {
	explained: 'Explained',
	mistake: 'Mistake',
	useful: 'Useful',
};

const REASON_COLOR: Record<TWordReason, string> = {
	explained: theme.palette.primary.main,
	mistake: theme.palette.error.main,
	useful: theme.palette.grey[400],
};

const SpeakButton: React.FC<{ text: string, langCode?: string }> = ({ text, langCode }) => (
	<IconButton
		size={'small'}
		onClick={() => textSpeaker.speak(text, langCode)}
		sx={{ color: theme.palette.grey[400], flexShrink: 0 }}
	>
		<SpeakerIcon sx={{ width: theme.spacing(2), height: theme.spacing(2) }} />
	</IconButton>
);

export const WordsTab: React.FC<IWordsTabProps> = ({
	audioId,
	audioLoaded,
	hasTranscript,
	langCode,
	loaded,
	loadFailed,
	words,
	canRegenerate,
	generating,
	generateFailed,
	captionTexts,
	lemmas,
	lemmasLoading,
	onLoad,
	onLoadLemmas,
	onLoadNative,
	nativeLangCode,
	nativeRequested,
	nativeLoading,
	wordsNative,
	onGenerate,
	onTimestampClick,
}) => {

	// audioId stays empty until both the route and the login have resolved after a reload
	React.useEffect(() => {
		if (audioId) onLoad();
	}, [audioId]);

	const [showForeign, setShowForeign] = React.useState(false);

	// no word list yet — generate it on open, for everyone; stops after a failure until Try again
	React.useEffect(() => {
		if (loaded && hasTranscript && !words.length && !generating && !generateFailed) onGenerate();
	}, [loaded, hasTranscript, words.length, generating, generateFailed]);

	// translation into the viewer's native language — once per word list; none when it's the lesson language
	React.useEffect(() => {
		if (words.length && nativeLangCode && langCode && nativeLangCode !== langCode && !nativeRequested) onLoadNative();
	}, [words, nativeLangCode, langCode, nativeRequested]);

	const [frequencyTab, setFrequencyTab] = React.useState<TFrequencyTab>('text');

	const tokenized = React.useMemo(() => tokenizeCaptions(captionTexts), [captionTexts]);
	const uniqueWords = React.useMemo(() => collectWords(tokenized), [tokenized]);

	React.useEffect(() => {
		onLoadLemmas(uniqueWords);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [langCode, uniqueWords.join(',')]);

	const groups = React.useMemo(() => groupWordsByLemma(tokenized, lemmas), [tokenized, lemmas]);

	if (!audioLoaded) {
		return (
			<Stack alignItems={'center'} justifyContent={'center'} sx={{ flex: '1 1 auto', minHeight: 0 }}>
				<CircularProgress size={theme.spacing(4)} />
			</Stack>
		);
	}

	if (!hasTranscript) {
		return (
			<Stack alignItems={'center'} justifyContent={'center'} sx={{ flex: '1 1 auto', minHeight: 0 }}>
				<Text sx={{ fontSize: theme.typography.subtitle1, color: theme.palette.grey[400] }}>
					Words can be shown once the recording has a transcript
				</Text>
			</Stack>
		);
	}

	const visibleGroups = groups.filter(g => !g.foreign);
	const foreignGroups = groups.filter(g => g.foreign);

	const buttonSx = {
		fontSize: theme.typography.subtitle1,
	}

	return (
		<Stack gap={2} sx={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }}>
			<Stack gap={1}>
				<Stack direction={'row'} alignItems={'center'} gap={1.5}>
					<Text
						sx={{
							fontSize: theme.typography.h6,
							fontWeight: 600,
						}}>
							Studied in this lesson
						</Text>
					{!!words.length && canRegenerate &&
					<Button
						size={'small'}
						disabled={generating}
						sx={buttonSx}
						onClick={onGenerate}
					>
						{generating ? 'Generating…' : 'Regenerate'}
					</Button>
					}
					{nativeLoading &&
					<Tooltip title={'Translating…'}>
						<Box sx={{ position: 'relative', display: 'flex', width: theme.spacing(3), height: theme.spacing(3) }}>
							<CircularProgress size={theme.spacing(3)} />
							<TranslationsIcon
								sx={{
									position: 'absolute',
									inset: 0,
									m: 'auto',
									width: theme.spacing(1.5),
									height: theme.spacing(1.5),
									color: theme.palette.primary.main,
								}}
							/>
						</Box>
					</Tooltip>
					}
				</Stack>

				{!loaded && loadFailed &&
				<Stack direction={'row'} alignItems={'center'} gap={1.5}>
					<Text sx={{ color: theme.palette.grey[400] }}>Failed to load the word list</Text>
					<Button
						size={'small'}
						sx={buttonSx}
						onClick={onLoad}
					>Try again</Button>
				</Stack>
				}

				{!loaded && !loadFailed &&
				<Stack alignItems={'center'} sx={{ p: theme.spacing(2) }}>
					<CircularProgress size={theme.spacing(3)} />
				</Stack>
				}

				{loaded && !words.length &&
				<Stack direction={'row'} alignItems={'center'} gap={1}>
					{generateFailed
						? <>
							<Text sx={{ color: theme.palette.grey[400] }}>Failed to prepare the word list</Text>
							<Button
								size={'small'}
								sx={buttonSx}
								onClick={onGenerate}
							>Try again</Button>
						</>
						: <>
							<CircularProgress size={theme.spacing(2)} />
							<Text sx={{ color: theme.palette.grey[400] }}>Preparing word list…</Text>
						</>
					}
				</Stack>
				}

				{!!words.length &&
				<Stack gap={1}>
					{words.map((item, index) => (
						<Paper key={index} sx={{ p: theme.spacing(1.25), background: theme.palette.grey[50] }}>
							<Stack direction={'row'} alignItems={'flex-start'} gap={1}>
								<Stack sx={{ flex: '1 1 auto', minWidth: 0 }} gap={.25}>
									<Stack direction={'row'} alignItems={'center'} gap={.5}>
										<SpeakButton text={item.word} langCode={langCode} />
										<NativeTooltip text={wordsNative?.[index]?.wordNative}>
											<Box component={'span'}>
												<Text sx={{
													fontWeight: 600,
												}}>
													{item.word}
												</Text>
											</Box>
										</NativeTooltip>
										<Box
											sx={{
												ml: .5,
												px: .75,
												borderRadius: theme.spacing(1),
												border: `1px solid ${REASON_COLOR[item.reason]}`,
												color: REASON_COLOR[item.reason],
												fontSize: '11px',
												lineHeight: theme.spacing(2),
												whiteSpace: 'nowrap',
											}}
										>
											{REASON_LABEL[item.reason]}
										</Box>
										{item.timestamp !== undefined &&
										<TimestampButton time={item.timestamp} onClick={onTimestampClick} sx={{ ml: 'auto' }} />
										}
									</Stack>
									{item.meaning &&
										<NativeTooltip text={wordsNative?.[index]?.meaningNative}>
											<Box component={'span'}>
												<Text sx={{
													color: theme.palette.grey[500],
												}}>
													{item.meaning}
												</Text>
											</Box>
										</NativeTooltip>
									}
									{item.quote &&
										<Text
											sx={{
												color: theme.palette.grey[900],
												fontSize: theme.typography.subtitle2,
											}}
										>
											“{item.quote}”
										</Text>
									}
								</Stack>
							</Stack>
						</Paper>
					))}
				</Stack>
				}
			</Stack>

			{lemmasLoading &&
			<Stack direction={'row'} alignItems={'center'} gap={1}>
				<CircularProgress size={theme.spacing(2)} />
				<Text sx={{ color: theme.palette.grey[400], fontSize: theme.typography.subtitle2 }}>Grouping word forms…</Text>
			</Stack>
			}

			{!!visibleGroups.length &&
			<Stack gap={1.5}>
				<Stack
					gap={1.5}
					rowGap={1}
					direction={'row'}
					alignItems={'center'}
					flexWrap={'wrap'}
				>
					<Box
						sx={{
							fontSize: theme.typography.h6,
							fontWeight: 600,
						}}
					>
						Frequent words
					</Box>
					<Tabs
						value={frequencyTab}
						onChange={(_, tab: TFrequencyTab) => setFrequencyTab(tab)}
						sx={pillTabsSx}
					>
						<Tab label={'In this lesson'} value={'text'} />
						<Tab label={'In the language'} value={'language'} />
					</Tabs>
				</Stack>

				{(frequencyTab === 'text' ? TEXT_BUCKET_LABELS : LANGUAGE_BUCKET_LABELS).map((label, bucket) => {
					const bucketGroups = visibleGroups.filter(g => (frequencyTab === 'text' ? g.textBucket : g.languageBucket) === bucket);
					if (!bucketGroups.length) return null;
					return (
						<Stack key={label} gap={.5}>
							<Text sx={{ color: theme.palette.grey[400], fontSize: theme.typography.subtitle2 }}>{label}</Text>
							<Stack direction={'row'} sx={{ flexWrap: 'wrap' }} gap={.75}>
								{bucketGroups.map(group => (
									<Box
										key={group.lemma}
										component={'button'}
										onClick={() => textSpeaker.speak(group.forms[0].word, langCode)}
										sx={{
											border: 'none',
											borderRadius: theme.spacing(2),
											background: theme.palette.grey[100],
											color: theme.palette.grey[700],
											p: theme.spacing(.5, 1.25),
											cursor: 'pointer',
											fontSize: theme.typography.subtitle2,
											'&:hover': { background: theme.palette.grey[200] },
										}}
									>
										{group.forms[0].word} · {group.total}
									</Box>
								))}
							</Stack>
						</Stack>
					);
				})}
			</Stack>
			}

			{!!foreignGroups.length &&
			<Stack gap={.5}>
				<Box
					component={'button'}
					onClick={() => setShowForeign(!showForeign)}
					sx={{ border: 'none', background: 'none', p: 0, cursor: 'pointer', color: theme.palette.grey[400], textAlign: 'left' }}
				>
					{showForeign ? '▾' : '▸'} Other languages ({foreignGroups.length})
				</Box>
				{showForeign &&
				<Stack direction={'row'} sx={{ flexWrap: 'wrap' }} gap={.75}>
					{foreignGroups.map(group => (
						<Box
							key={group.lemma}
							component={'button'}
							// language unknown per word — mostly code-switch into the viewer's native language
							onClick={() => textSpeaker.speak(group.forms[0].word, nativeLangCode || langCode)}
							sx={{
								border: 'none',
								borderRadius: theme.spacing(2),
								background: theme.palette.grey[100],
								color: theme.palette.grey[400],
								p: theme.spacing(.5, 1.25),
								cursor: 'pointer',
								fontSize: theme.typography.subtitle2,
							}}
						>
							{group.forms[0].word} · {group.total}
						</Box>
					))}
				</Stack>
				}
			</Stack>
			}
		</Stack>
	);
};
